import React, { useEffect, useState } from "react";
import { motion, animate } from "framer-motion";

/**
 * KpiCard V2 — éditorial.
 * Chiffre géant très fin, micro-label uppercase, sparkline monoline très longue,
 * delta discret sous le chiffre. Padding généreux, ombre douce.
 */
function useCountUp(value, duration = 1.1) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(0, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [value, duration]);
  return display;
}

function Sparkline({ data = [], color = "#151515" }) {
  const w = 240;
  const h = 36;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const points = data
    .map((v, i) => `${i * step},${h - ((v - min) / range) * (h - 4) - 2}`)
    .join(" ");
  const pathD = `M ${points.split(" ").join(" L ")}`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height: "36px" }}
      aria-hidden
    >
      <motion.path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
      />
    </svg>
  );
}

export function KpiCard({
  label,
  value,
  unit = "",
  delta = null,
  spark = [],
  isPrimary = false,
  className = "",
  testid,
}) {
  const count = useCountUp(value);
  const isInt = Number.isInteger(value);
  const formatted = isInt
    ? Math.round(count).toLocaleString("fr-FR")
    : count.toFixed(1);
  const positive = delta && delta.startsWith("+");

  return (
    <div
      data-testid={testid}
      className={`shadow-wyvio-elev relative flex flex-col justify-between rounded-[22px] bg-white transition-shadow duration-300 hover:shadow-wyvio-elev-hover ${className}`}
      style={{ minHeight: "220px" }}
    >
      <div className="p-8 pb-4">
        {/* Micro-label + indicateur focus */}
        <div className="flex items-center justify-between">
          <span className="text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#8A8A80]">
            {label}
          </span>
          {isPrimary && (
            <span
              aria-hidden
              className="wyvio-segment-active inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: "#C8FF2F" }}
            />
          )}
        </div>

        {/* Chiffre géant éditorial */}
        <div className="mt-10 flex items-baseline gap-2">
          <span className="wyvio-tabular text-[64px] font-medium leading-[0.9] tracking-[-0.035em] text-[#151515]">
            {formatted}
          </span>
          {unit && (
            <span className="wyvio-tabular text-[18px] font-normal text-[#8A8A80]">
              {unit}
            </span>
          )}
        </div>

        {/* Delta discret — juste la variation, pas de texte parasite */}
        {delta && (
          <div className="mt-4">
            <span
              className="wyvio-tabular text-[12.5px] font-medium"
              style={{ color: positive ? "#4A6B00" : "#B7292E" }}
            >
              {delta}
            </span>
          </div>
        )}
      </div>

      {/* Sparkline full width en bas */}
      {spark.length > 0 && (
        <div className="px-2 pb-3">
          <Sparkline data={spark} color={isPrimary ? "#151515" : "#C4C4B8"} />
        </div>
      )}
    </div>
  );
}
